import { useState, useEffect } from 'react';
import { useStepData } from '@/hooks/useStepData';
import { Stepper, StepperButtons, StepperContent, StepperProgress } from '@/components/Stepper';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/context/AuthContext';
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import Layout from '@/components/Layout';
import ChoiceStep from '@/components/OnboardingSteps/ChoiceStep';
import UserInfoStep from '@/components/OnboardingSteps/UserInfoStep';
import OrganizationStep from '@/components/OnboardingSteps/OrganizationStep';
import InviteStep from '@/components/OnboardingSteps/InviteStep';

const Onboarding = () => {
    const navigate = useNavigate();
    const { user, setUser, axiosInstance } = useAuth();
    const { toast } = useToast();
    
    let initialSteps = [
        {
            key: 'choice',
            name: 'Get Started',
            data: '',
            isComplete: false,
            disabled: false,
            validation: [
                { field: 'choice', required: true }
            ],
            component: ChoiceStep
        },
        {
            key: 'organization',
            name: 'Organization',
            data: {
                name: '',
                slug: ''
            },
            isComplete: false,
            disabled: false,
            validation: [
                { field: 'name', required: true },
                { field: 'slug', required: true }
            ],
            component: OrganizationStep
        },
        {
            key: 'userInfo',
            name: 'Personal Details',
            data: {
                name: '',
                avatarColor: '',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                primaryFeature: '',
                referralSource: ''
            },
            isComplete: false,
            disabled: false,
            validation: [
                { field: 'name', required: true }
            ],
            component: UserInfoStep
        },
        {
            key: 'invite',
            name: 'Invite Team',
            data: {
                teamEmails: ''
            },
            isComplete: false,
            disabled: false,
            validation: [],
            component: InviteStep
        }
    ];

    const checkOnboardingStatus = async () => {
        try {
            const response = await axiosInstance.get('/users/onboarding-status');
            const { hasUserInfo, hasOrganization, canInviteMembers } = response.data;

            if (hasOrganization) {
                // Skip to user info step
                setCurrentStep(2);

                // Disable step 0 (choice) and step 1 (organization)
                initialSteps[0].disabled = true;
                initialSteps[1].disabled = true;
            }

            if (!canInviteMembers) {
                // Delete invite step (step 3)
                initialSteps.pop();
            }

        } catch (error) {
            console.error('Error checking onboarding status:', error);
        } finally {
            setLoadingOnboardingStatus(false);
        }
    };

    useEffect(() => {
        checkOnboardingStatus();
    }, []);

    const { steps, updateStepData, getStepData, isStepComplete } = useStepData(initialSteps);
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingOnboardingStatus, setLoadingOnboardingStatus] = useState(true);

    const handleSubmit = async () => {
        const choice = getStepData('choice');
        
        if (choice === 'join') {
            navigate('/login');
            return;
        }

        setLoading(true);
        try {
            const orgData = getStepData('organization');
            const userData = getStepData('userInfo');
            const inviteData = getStepData('invite');
            
            const response = await axiosInstance.post('/organizations/onboarding', {
                userName: userData?.name || null,
                userAvatarColor: userData?.avatarColor || null,
                userTimezone: userData?.timezone || null,
                orgSlug: orgData?.slug || null,
                orgName: orgData?.name || null,
                inviteTeamEmails: inviteData?.teamEmails?.split(',').map(email => email.trim()).filter(Boolean) || [],
            });

            const { organization: updatedOrg, user: updatedUser } = response.data;

            setUser({ ...user, ...updatedUser });
            
            if (orgData && orgData.name) {
                toast({
                    description: `${updatedOrg.name} created successfully!`,
                    variant: "default",
                });
            } else {
                toast({
                    description: `Onboarding completed successfully!`,
                    variant: "default",
                });
            }

            navigate('/today');
        } catch (error) {
            console.error('Error:', error);
            toast({
                description: "Failed to complete setup",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    if (loadingOnboardingStatus) {
        return (
            <Layout>
                <LoadingState />
            </Layout>
        );
    }

    return (
        <Layout noContainer>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
            >
                <Stepper steps={steps} currentStep={currentStep} setCurrentStep={setCurrentStep}>
                    <Card className="border-none shadow-none flex flex-col justify-between h-[calc(100vh-4rem)]">
                        <CardHeader className="border-b">
                            <StepperProgress />
                        </CardHeader>
                        <CardContent className="flex-grow overflow-hidden p-0">
                            <ScrollArea className="h-full w-full">
                                <div className="mx-6 my-8">
                                    <StepperContent>
                                        {steps.map((step) => {
                                            const StepComponent = step.component;
                                            return (
                                                <StepComponent
                                                    key={step.key}
                                                    stepKey={step.key}
                                                    updateStepData={updateStepData}
                                                    getStepData={getStepData}
                                                    isComplete={isStepComplete(step.key)}
                                                />
                                            );
                                        })}
                                    </StepperContent>
                                </div>
                            </ScrollArea>
                        </CardContent>
                        <CardFooter className="border-t p-6">
                            <StepperButtons 
                                onSubmit={handleSubmit} 
                                loading={loading} 
                                submitButtonText="Complete Setup" 
                            />
                        </CardFooter>
                    </Card>
                </Stepper>
            </motion.div>
        </Layout>
    );
};

const LoadingState = () => (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center space-x-4 animate-pulse">
        <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
        <h1 className="text-xl font-semibold">Preparing onboarding...</h1>
    </div>
);

export default Onboarding; 